import type { ReactNode } from 'react';

import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '../../lib/cn';
import type { ZIndex } from '../../tokens/z-index';

// ---- Root ----
export interface PopoverProps extends PopoverPrimitive.PopoverProps {
  children: ReactNode;
}

export function Popover({ children, ...rest }: PopoverProps) {
  return <PopoverPrimitive.Root {...rest}>{children}</PopoverPrimitive.Root>;
}

// ---- Trigger ----
export interface PopoverTriggerProps extends PopoverPrimitive.PopoverTriggerProps {}

export function PopoverTrigger({ children, ...rest }: PopoverTriggerProps) {
  return (
    <PopoverPrimitive.Trigger asChild {...rest}>
      {children}
    </PopoverPrimitive.Trigger>
  );
}

// ---- Content ----
export interface PopoverContentProps extends PopoverPrimitive.PopoverContentProps {
  /**
   * Override do tier de z-index do conteúdo portal-rendered, aplicado via style
   * inline (vence a classe `z-[500]`). Raramente necessário: o default
   * `Z.FLOATING` (500) já abre acima de qualquer modal (Z.MODAL = 400).
   */
  zIndex?: ZIndex;
}

export function PopoverContent({
  children,
  className,
  align = 'center',
  sideOffset = 4,
  zIndex,
  style,
  ...rest
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        style={zIndex != null ? { ...style, zIndex } : style}
        className={cn(
          // Z.FLOATING (500) — portal-rendered floating UI sits above any modal
          // (400) by default, so a Popover inside a Dialog/Sheet is never hidden
          // behind the backdrop. No per-call-site config needed.
          'z-[500] min-w-[12rem] rounded-lg border border-slate-200 bg-white p-3 shadow-md outline-none',
          'animate-in fade-in-0 zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...rest}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}
